import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { formatInTimeZone } from 'date-fns-tz';
import Image from 'next/image';
import { useRouter } from 'next/router';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { Box, Button, Typography } from '@mui/material';
import { trackEvent } from '@surfline/web-common';

import config from 'config';
import PadlockIcon from 'components/Icons/Padlock';
import SpinnerMini from 'components/SpinnerMini';
import useCams from 'hooks/useCams';
import useCheckCameraSupportsSmartRewinds from 'hooks/useCheckCameraSupportsSmartRewinds';
import { getSpotDetails, getSpotReportAssociated } from 'selectors/spot';
import { useUserDateFormat, useUserPermissionStatus } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import { SpotDetails } from 'types/spot';
import {
  BaseRewindPlaylistBlock,
  ClipsPlaylistBlock,
  SmartRewindPlaylistBlock,
} from 'utils/rewinds/playlistBlocks.types';

import { getDayString } from 'utils/getDayString';
import RewindSelectorPlayIcon from './RewindSelectorPlayIcon';

import styles from './RewindSelector.module.scss';

const MAX_DAYS_REWINDS = 5;

const UPGRADE_URL = `${config.surflineHost}/upgrade`;

const RewindSelectorPaywallButton: React.FC<{
  segmentProps: {
    location: string;
    spotId: string;
    spotName: string;
    subregionId: string;
  };
}> = ({ segmentProps }) => {
  const onClickHandler = useCallback(() => {
    trackEvent('Clicked Subscribe CTA', segmentProps);
    window.location.href = UPGRADE_URL;
  }, [segmentProps]);

  return (
    <button
      className={styles.paywallButton}
      data-testid="rewind-selector-paywall-button"
      onClick={onClickHandler}
      type="button"
    >
      <Box className={styles.paywallButtonLeft}>
        <Box className={styles.paywallIconWrapper}>
          <PadlockIcon />
        </Box>
        <Typography component="span" variant="callout2">
          Get five days of Rewind footage.
        </Typography>
      </Box>
      <Box className={styles.paywallButtonRight}>
        <Typography component="span" variant="callout2" className={styles.paywallTryPremium}>
          Try Premium
        </Typography>
      </Box>
    </button>
  );
};

const PaywallButton = memo(RewindSelectorPaywallButton);

export const RewindSelector: React.FC<{
  currentPlaylist: SmartRewindPlaylistBlock | ClipsPlaylistBlock;
  disablePaywalls?: boolean;
  extraSegmentProps?: Record<string, unknown>;
  expandedRewindsError?: boolean;
  expandedRewindsLoading?: boolean;
  isLive?: boolean;
  loadExpandedRewinds?: () => Promise<void>;
  onRewindSelection?: () => void;
  rewindsPlaylists: Map<string, BaseRewindPlaylistBlock>;
  updateCurrentPlaylist: (currentPlaylistTime: string) => void;
}> = ({
  currentPlaylist,
  disablePaywalls = false,
  extraSegmentProps,
  expandedRewindsError = false,
  expandedRewindsLoading = false,
  isLive,
  loadExpandedRewinds,
  onRewindSelection,
  rewindsPlaylists,
  updateCurrentPlaylist,
}) => {
  const { hasCamRewindPermissions } = useUserPermissionStatus();
  const spotReportAssociated = useAppSelector(getSpotReportAssociated);
  const spot: SpotDetails = useAppSelector(getSpotDetails);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeButton, setActiveButton] = useState<HTMLButtonElement | null>(null);
  const { primaryCam } = useCams(spot?.cameras, spot._id);
  const supportsSmartRewinds = useCheckCameraSupportsSmartRewinds(primaryCam, spot);
  const userDateFormat = useUserDateFormat() || 'MDY';
  const { query } = useRouter();
  const isNative = query?.native?.toString() === 'true';

  const timezone = spotReportAssociated?.timezone ?? 'America/Los_Angeles';

  const pageLoadNow = useMemo(() => new Date(), []);

  const dateFormat = useMemo(
    () => (userDateFormat === 'MDY' ? 'EEE, M/d' : 'EEE, d/M'),
    [userDateFormat],
  );

  const currentDate = useMemo(
    () => formatInTimeZone(currentPlaylist.bucketStart, timezone, dateFormat),
    [currentPlaylist, timezone, dateFormat],
  );

  const [selectedDate, setSelectedDate] = useState(currentDate);

  const onClickRewindSelectorHandler = useCallback(() => setMenuOpen((curr: boolean) => !curr), []);

  const onClickBackdropHandler = useCallback(() => setMenuOpen(false), []);

  const onRetryExpandedRewinds = useCallback(() => {
    void loadExpandedRewinds?.();
  }, [loadExpandedRewinds]);

  const groupedRewindsPlaylistsByDay = useMemo(
    () =>
      Array.from(rewindsPlaylists.values()).reduce(
        (groupedRewindPlaylistAccumulator, playlist): Map<string, [BaseRewindPlaylistBlock]> => {
          const date = formatInTimeZone(playlist.bucketStart, timezone, dateFormat);

          if (
            !groupedRewindPlaylistAccumulator.has(date) &&
            Array.from(groupedRewindPlaylistAccumulator.keys()).length < MAX_DAYS_REWINDS
          ) {
            groupedRewindPlaylistAccumulator.set(date, [playlist]);
          } else {
            groupedRewindPlaylistAccumulator.get(date)?.push(playlist);
          }

          return groupedRewindPlaylistAccumulator;
        },
        new Map() as Map<string, [BaseRewindPlaylistBlock]>,
      ),
    [rewindsPlaylists, timezone, dateFormat],
  );

  const playlistDates = useMemo(
    () => Array.from(groupedRewindsPlaylistsByDay.keys()),
    [groupedRewindsPlaylistsByDay],
  );

  const selectedDatePlaylists = useMemo(
    () => groupedRewindsPlaylistsByDay.get(selectedDate),
    [groupedRewindsPlaylistsByDay, selectedDate],
  );

  const trackRewindSelectorEvent = useCallback(
    (currentPlaylistTime: Date) => {
      const startTime = new Date(currentPlaylistTime);
      startTime.setMinutes(0, 0, 0);

      const clipStartTime = formatInTimeZone(startTime, timezone, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");

      trackEvent('Played Clip', {
        daysAgo: Math.round((Date.now() - currentPlaylistTime.getTime()) / (1000 * 60 * 60 * 24)),
        category: 'KBYG Rewinds',
        pageName: 'KBYG Rewinds',
        premiumCam: primaryCam?.isPremium,
        smartCam: supportsSmartRewinds,
        lineupCam: !!primaryCam?.isLineupCam,
        spotId: spot?._id,
        spotName: spot?.name,
        camId: primaryCam?._id,
        camName: primaryCam?.alias,
        button: 'Rewind Selector',
        clipStartTime,
        ...extraSegmentProps,
      });
    },
    [extraSegmentProps, primaryCam, spot, supportsSmartRewinds, timezone],
  );

  const isBetweenOneAndTenMinutes = useMemo(() => {
    if (!selectedDatePlaylists?.[0]?.bucketEnd) return false;
    const differenceInMins =
      (pageLoadNow.getTime() - selectedDatePlaylists[0].bucketEnd.getTime()) / (1000 * 60);
    return differenceInMins > 1 && differenceInMins < 10;
  }, [selectedDatePlaylists, pageLoadNow]);

  const segmentPropsPaywallToday = useMemo(
    () => ({
      location: 'Wave Timeline Selector Today',
      spotId: spot?._id,
      spotName: spot?.name,
      subregionId: spot?.subregion?._id,
    }),
    [spot?._id, spot?.name, spot?.subregion?._id],
  );

  const segmentPropsPaywallPreviousDay = useMemo(
    () => ({
      location: 'Wave Timeline Selector Previous Day',
      spotId: spot?._id,
      spotName: spot?.name,
      subregionId: spot?.subregion?._id,
    }),
    [spot?._id, spot?.name, spot?.subregion?._id],
  );

  const dateButtons = useMemo(
    () =>
      playlistDates.map((day: string) => {
        const dayStr = getDayString(day, timezone, dateFormat);
        const isToday = dayStr === 'Today';
        const isYesterday = dayStr === 'Yesterday';

        return (
          <Button
            size="small"
            key={day}
            onClick={() => setSelectedDate(day)}
            className={classNames(
              styles.dateButton,
              day === selectedDate && styles.dateButtonActive,
              (isToday || isYesterday) && styles.namedDay,
            )}
            data-testid="rewind-selector-date-button"
          >
            {dayStr}
          </Button>
        );
      }),
    [dateFormat, playlistDates, selectedDate, timezone],
  );

  const selectButtons = useMemo(
    () =>
      selectedDatePlaylists?.map((playlist, index) => {
        const { bucketStart, bucketEnd, rewinds } = playlist;
        const camStillUrl = rewinds?.[0]?.thumbSmallUrl;
        const active = bucketEnd === currentPlaylist.bucketEnd;

        const newDateStart =
          selectedDate !== currentDate && index === 0 && selectedDate !== playlistDates[0];

        const className = classNames({
          [styles.timeButton]: true,
          [styles.timeButtonActive]: active,
          [styles.timeButtonInactive]: !rewinds.length && !active,
        });

        const clickAction = rewinds.length
          ? () => {
              if (hasCamRewindPermissions || disablePaywalls) {
                updateCurrentPlaylist(new Date(bucketEnd).toISOString());
                setMenuOpen(false);
                trackRewindSelectorEvent(bucketStart);
                if (onRewindSelection) {
                  onRewindSelection();
                }
              } else {
                trackEvent('Clicked Subscribe CTA', {
                  location: 'Wave Timeline Selector Time Button',
                  spotId: spot?._id,
                  spotName: spot?.name,
                  subregionId: spot?.subregion?._id,
                });
                window.location.href = UPGRADE_URL;
              }
            }
          : undefined;

        return (
          <Fragment key={bucketStart.toString()}>
            {index === 1 && !hasCamRewindPermissions && !disablePaywalls && (
              <PaywallButton segmentProps={segmentPropsPaywallToday} />
            )}
            <button
              className={className}
              data-testid="rewind-selector-time-button"
              disabled={!rewinds.length}
              onClick={clickAction}
              ref={active || newDateStart ? setActiveButton : undefined}
              type="button"
            >
              <Box className={styles.timeButtonLeft}>
                <Box className={styles.timeButtonCamStill}>
                  {active && <RewindSelectorPlayIcon className={styles.timeButtonPlay} />}
                  {camStillUrl && (
                    <Image
                      src={camStillUrl}
                      alt={`Rewind Camera Still for ${bucketStart.toString()}`}
                      layout="fill"
                      objectFit="cover"
                      objectPosition="center"
                      unoptimized
                      loading="lazy"
                      className={styles.timeButtonImage}
                    />
                  )}
                </Box>
                <Typography
                  variant="callout2"
                  className={styles.textColor}
                  data-testid="rewind-selector-time-button-text"
                >
                  {formatInTimeZone(bucketStart, timezone, 'h:mma').toLowerCase()} -{' '}
                  {formatInTimeZone(bucketEnd, timezone, 'h:mma').toLowerCase()}
                </Typography>
                {active && (
                  <Typography className={styles.activeText} variant="caption">
                    Active
                  </Typography>
                )}
                {!rewinds.length && (
                  <Typography
                    variant="caption"
                    className={classNames({
                      [styles.textColorWithMargin]: true,
                      [styles.unavailable]: true,
                    })}
                  >
                    Rewinds Unavailable
                  </Typography>
                )}
              </Box>
              <Box className={styles.timeButtonRight}>
                {!active && !hasCamRewindPermissions && !disablePaywalls && (
                  <Typography
                    className={styles.paywallTryPremium}
                    component="span"
                    data-testid="rewind-selector-time-button-paywall"
                    variant="callout2"
                  >
                    Try Premium
                  </Typography>
                )}
                <ChevronRight className={styles.timeButtonIcon} />
              </Box>
            </button>
          </Fragment>
        );
      }),
    [
      currentDate,
      currentPlaylist.bucketEnd,
      disablePaywalls,
      hasCamRewindPermissions,
      onRewindSelection,
      playlistDates,
      segmentPropsPaywallToday,
      selectedDate,
      selectedDatePlaylists,
      spot?._id,
      spot?.name,
      spot?.subregion?._id,
      timezone,
      trackRewindSelectorEvent,
      updateCurrentPlaylist,
    ],
  );

  useEffect(() => {
    if (activeButton) {
      activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      activeButton.focus();
    }
  }, [activeButton]);

  useEffect(() => {
    if (menuOpen) setSelectedDate(currentDate);
  }, [menuOpen, currentDate]);

  useEffect(() => {
    if (menuOpen) {
      void loadExpandedRewinds?.();
    }
  }, [loadExpandedRewinds, menuOpen]);

  return (
    <Box className={classNames({ [styles.rewindSelector]: true, 'sl-rewind-selector': true })}>
      <Button
        className={classNames([
          styles.button,
          !isLive && styles.buttonNonLive,
          menuOpen && styles.buttonActive,
        ])}
        data-testid="rewind-selector-button"
        disableFocusRipple
        disableRipple
        disableTouchRipple
        onClick={onClickRewindSelectorHandler}
        size="small"
        variant="secondary"
      >
        {`
          ${getDayString(currentDate, timezone, dateFormat).split(', ')[0]}, ${formatInTimeZone(
            currentPlaylist.bucketStart,
            timezone,
            'h:mma',
          ).toLowerCase()}
        `}
        {menuOpen ? <ExpandLess /> : <ExpandMore />}
      </Button>
      {menuOpen && (
        <>
          <div
            aria-hidden="true"
            className={styles.menuBackdrop}
            onClick={onClickBackdropHandler}
          />
          <Box
            data-testid="rewind-selector-modal"
            className={classNames({
              [styles.modal]: true,
              // [styles.modalNative]: isNative,
              ['sl-rewind-selector-modal']: true,
              ['sl-rewind-selector-modal--native']: isNative,
            })}
          >
            {expandedRewindsLoading ? (
              <Box className={styles.timeButtonContainer}>
                <Box
                  className={classNames(styles.timeButton, styles.generatingClipsPlaceHolder)}
                  data-testid="rewind-selector-expanded-loading"
                >
                  <SpinnerMini className={styles.timeButtonSpinner} />
                  <Typography variant="callout2" className={styles.textColor}>
                    Loading rewind history...
                  </Typography>
                </Box>
              </Box>
            ) : expandedRewindsError ? (
              <Box className={styles.timeButtonContainer}>
                <button
                  className={styles.timeButton}
                  data-testid="rewind-selector-expanded-retry"
                  onClick={onRetryExpandedRewinds}
                  type="button"
                >
                  <Box className={styles.timeButtonLeft}>
                    <Typography variant="callout2" className={styles.textColor}>
                      Rewind history failed to load.
                    </Typography>
                  </Box>
                  <Box className={styles.timeButtonRight}>
                    <Typography
                      className={styles.paywallTryPremium}
                      component="span"
                      variant="callout2"
                    >
                      Retry
                    </Typography>
                  </Box>
                </button>
              </Box>
            ) : (
              <>
                <Box className={styles.dateButtonsContainer}>
                  <Box className={styles.dateButtons}>{dateButtons}</Box>
                </Box>
                <Box className={styles.timeButtonContainer} key={`selectList-${selectedDate}`}>
                  {selectedDate === playlistDates[0] &&
                    selectedDatePlaylists?.length &&
                    isBetweenOneAndTenMinutes && (
                      <Box
                        ref={setActiveButton}
                        className={classNames(styles.timeButton, styles.generatingClipsPlaceHolder)}
                        data-testid="generating-clips-placeholder"
                      >
                        <SpinnerMini className={styles.timeButtonSpinner} />
                        <Typography variant="callout2" className={styles.textColor}>
                          {formatInTimeZone(
                            selectedDatePlaylists[0].bucketEnd,
                            timezone,
                            'h:mma',
                          ).toLowerCase()}{' '}
                          - {formatInTimeZone(pageLoadNow, timezone, 'h:mma').toLowerCase()}
                        </Typography>
                        <Typography variant="caption1" className={styles.textColorWithMargin}>
                          Generating Clips...
                        </Typography>
                      </Box>
                    )}
                  {hasCamRewindPermissions || disablePaywalls || selectedDate === currentDate ? (
                    selectButtons
                  ) : (
                    <PaywallButton segmentProps={segmentPropsPaywallPreviousDay} />
                  )}
                </Box>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default memo(RewindSelector);
